import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  Paper,
  Alert,
  Loader,
  ConfirmDialog,
  DetailField,
  ProductImageCarousel,
  Badge,
  Text,
} from '@vipso/shared';
import {
  useGetProductQuery,
  useDeleteProductMutation,
  useGetProductInventoryCountsQuery,
} from '../api/tenantApi';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';
import { useModuleTerminology } from '../hooks/useModuleLabel';
import { useFileViewer } from '../features/files/FileViewerContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';

export function ProductDetailPage() {
  const access = useModuleAccess('product');
  const approvalAccess = useApprovalModuleAccess();
  const { t } = useTranslation();
  const { openFileViewer } = useFileViewer();
  const terms = useModuleTerminology('product');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetProductQuery(id!, { skip: !id });
  const { data: inventoryData } = useGetProductInventoryCountsQuery(
    { productId: id! },
    { skip: !id },
  );
  const [remove, { isLoading: deleting }] = useDeleteProductMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const product = data?.getProduct;
  const counts = inventoryData?.getProductInventoryCounts;

  async function handleDelete() {
    if (!product) return;
    await remove(product.id).unwrap();
    setConfirmOpen(false);
    navigate('/products');
  }

  if (isLoading && !data) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  if (isError || !product) {
    return (
      <Alert color="red" title={t('terminology.notFound', { entity: terms.singular })}>
        <Button variant="light" mt="sm" onClick={() => navigate('/products')}>
          {t('terminology.backToList', { entity: terms.plural })}
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={product.name}
        description={t('terminology.details', { entity: terms.singular })}
        actions={
          <Group>
            <Button variant="default" onClick={() => navigate('/products')}>
              {t('common.back')}
            </Button>
            {access.canUpdate ? (
              <Button variant="light" onClick={() => navigate(`/products/${product.id}/edit`)}>
                {t('common.edit')}
              </Button>
            ) : null}
            {access.canDelete ? (
              <Button color="red" variant="light" onClick={() => setConfirmOpen(true)}>
                {t('common.delete')}
              </Button>
            ) : null}
          </Group>
        }
      />

      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          <ProductImageCarousel
            name={product.name}
            images={product.images ?? []}
            fallbackUrl={product.imageUrl}
            height={220}
            onImageClick={(image, index) => {
              const gallery = product.images?.length
                ? product.images
                : product.imageFileId
                  ? [{ fileId: product.imageFileId, url: product.imageUrl }]
                  : [];
              const siblings = gallery
                .map((img) => img.fileId)
                .filter((fid): fid is string => fid != null && String(fid).length > 0)
                .map(String);
              if (image.fileId) {
                openFileViewer({
                  fileId: image.fileId,
                  siblings,
                  siblingIndex: index,
                });
                return;
              }
              if (image.url) {
                openFileViewer({ url: image.url, siblings, siblingIndex: index });
              }
            }}
          />
          <DetailField label={t('products.unit')} value={product.unit} />
          <DetailField label={t('products.category')} value={product.category?.name} />
          {approvalAccess.product ? (
            <DetailField
              label={t('approval.status')}
              value={<ApprovalStatusBadge status={product.approvalStatus} />}
            />
          ) : null}
          <DetailField label={t('products.productDescription')} value={product.description} />
          <DetailField
            label={t('products.quantity')}
            value={product.quantity != null ? String(product.quantity) : undefined}
          />
          {counts ? (
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                {t('inventory.stockCounts')}
              </Text>
              <Group gap="xs" wrap="wrap">
                <Badge variant="light" color="blue">
                  {t('inventory.purchasePool')}: {counts.purchasePool}
                </Badge>
                <Badge variant="light" color="green">
                  {t('inventory.inHouse')}: {counts.inHouse}
                </Badge>
                <Badge variant="light" color="yellow">
                  {t('inventory.inApproval')}: {counts.inApproval}
                </Badge>
              </Group>
            </Stack>
          ) : null}
          <DetailField
            label={t('products.pricePerQuantity')}
            value={
              product.pricePerQuantity != null
                ? `₹${Number(product.pricePerQuantity).toFixed(2)}`
                : undefined
            }
          />
          <DetailField
            label={t('products.subsequentQuantity')}
            value={product.showSubse ? t('common.yes') : t('common.no')}
          />
          <DetailField
            label={t('products.addOnItem')}
            value={product.addOnItem ? t('common.yes') : t('common.no')}
          />
          {product.addOnItem ? (
            <DetailField label={t('products.addOnItemName')} value={product.addOnItemName} />
          ) : null}
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="PRODUCT"
        subjectId={product.id}
        entityApprovalStatus={product.approvalStatus}
        enabled={approvalAccess.product}
      />

      <Group>
        <Button onClick={() => navigate(`/orders?productIds=${product.id}`)}>
          {t('common.transactions')}
        </Button>
        <Button variant="default" onClick={() => navigate('/orders')}>
          {t('products.addRecord')}
        </Button>
      </Group>

      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDelete()}
        title={t('terminology.deleteConfirm', { entity: terms.singular })}
        message={t('common.deleteMessage', { name: product.name })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </Stack>
  );
}
